import { useEffect, useEffectEvent, useRef, useState } from 'react';

import type { AgentHitType } from '~/types/agent';

import SearchCount from '@helsenorge/core-cms/search-v2/page/components/search-count';
import SearchHits from '@helsenorge/core-cms/search-v2/page/components/search-hits';
import SearchLoadMoreButton from '@helsenorge/core-cms/search-v2/page/components/search-load-more';
import SearchStatus from '@helsenorge/core-cms/search-v2/page/components/search-status';
import SearchTitle from '@helsenorge/core-cms/search-v2/page/components/search-title';
import { setSearchParams } from '@helsenorge/core-cms/search-v2/page/utils';
import SearchInput from '@helsenorge/core-cms/search-v2/search-input';

import LoadingText from '../loading-text';
import SearchHit from '../search-hit';
import SearchHitsPlaceholder from '../search-hits-placeholder';
import SearchStatusText from '../search-status-text';
import Summary from '../summary';

import { useAgentSearch } from '~/cmsagent/hooks/use-agent-search';
import { useExpandedSummary } from '~/cmsagent/hooks/use-expanded-summary';
import { usePaginatedSearch } from '~/cmsagent/hooks/use-paginated-search';
import {
  trackGlobalSearch,
  trackPositiveFeedbackForSummary,
  trackNegativeFeedbackForSummary,
  trackResultClick,
  trackExpandedSummary,
} from '~/cmsagent/utils/analytics';
import { combineSearchResults } from '~/cmsagent/utils/combine-search-results';
import { parseSokekilde, sokekildeSearchParam, type Sokekilde } from '~/cmsagent/utils/sokekilde';
import { resolveInitialSummaryEnabled, saveSummaryChoice } from '~/cmsagent/utils/summary-preference';

export interface SearchPageResources {
  // Sidetittel, før søkeord er lagt til
  title: string;
  // Sidetittel, med {query} som plassholder for søkeordet
  searchResultTitle: string;
  // Tekst som vises når det ikke er noen treff
  noHitsText: string;
  // Lenketekst når det ikke er noen treff
  noHitsLinkTitle?: string;
  // Aria-label for søkeinputfeltet
  inputAriaLabel: string;
  // Aria-label for containeren rundt søkeinputfeltet og forslagene
  containerAriaLabel?: string;
  // Tekst for søke-knappen
  submitButtonText: string;
  // Tekst for knapp som laster flere treff
  showMoreButtonText: string;
  // Tekst som vises når søket starter
  loadingstart: string;
  // Tekst som vises mens søket pågår
  loadinginprogress: string;
  // Tekst som vises når søket avsluttes
  loadingend: string;
  // Feilmelding som vises ved teknisk feil under søk
  searchErrorMessage: string;
  // Feilmelding som vises når det ikke er skrevet inn noe søkeord
  noQueryErrorMessage: string;
  // Statusmelding som viser antall treff, med {totalHits} og {query} som plassholdere
  searchResultsStatusMessage: string;
  // Tekst for treff av typen "Tjeneste"
  tjenesteHitTypeText: string;
  // Tekst for treff av typen "Verktøy"
  verktoyHitTypeText: string;
  // Tekst som brukes på lenke til tjenesten
  goToTjeneste: string;
  // Tekst som brukes på lenke til verktøyet
  goToVerktoy: string;
  // Tekst for paginering, med {hits} og {totalHits} som plassholdere
  paginationMessage: string;
  // Tekst til brødsmulesti etter at man klikker på et søkeresultat
  returnUrlText: string;
  // Beta-tag for søkesiden
  betaTag: string;
  // Tekst for å vise oppsummering
  showSummary: string;
  // Tekst for mer informasjon om oppsummering
  moreInformation: string;
  // Tekst for lukk-knapp på oppsummering
  closeButton: string;
  // Tekst for å vise mer i oppsummering
  showMore: string;
  // Tekst for å vise mindre i oppsummering
  showLess: string;
  // Tekst som vises når oppsummering forstår spørsmålet
  loadingSummaryUnderstanding: string;
  // Tekst som vises når oppsummering henter relevant informasjon
  loadingSummaryFetching: string;
  // Tekst som vises når oppsummering optimaliserer informasjon
  loadingSummaryOptimizing: string;
  // Tekst som vises når oppsummering lager sammendrag av tekst
  loadingSummaryGenerating: string;
  // Tekst som forklarer hva oppsummering er
  summaryExplainedText: string;
  // Tittel for mer informasjon om oppsummering
  moreInformationTitle: string;
  // Tekst for mer informasjon om oppsummering
  moreInformationText: string;
  // Overskrift for oppsummeringsseksjonen
  summaryTitle: string;
  // Overskrift for lenker i oppsummeringen
  linksTitle: string;
  // Overskrift for handlinger i oppsummeringen
  actionsTitle: string;
  // Melding som vises når det ikke er nok informasjon til å lage en oppsummering
  missingInformationMessage: string;
  // Tekst for å spørre om oppsummeringen var nyttig
  wasSummaryHelpful: string;
  // Aria-label for "ja"-knappen i nyttighetsfeedback
  summaryWasHelpfulAriaLabel: string;
  // Aria-label for "nei"-knappen i nyttighetsfeedback
  summaryWasNotHelpfulAriaLabel: string;
}

interface SearchPageProps {
  // Relativ URL til søkesiden
  searchUrl: string;
  // Søkeord
  query: string;
  // Språket som siden er på
  language: string;
  // Innholdsreferanse
  contentReference: string | null;
  // Sidereferanse
  referrer: string | null;
  // Deaktiver autosuggest-funksjonalitet
  disableAutoSuggest?: boolean;
  // Type av søk, brukt for tracking i webstatistikk
  searchType: string;
  // URL til side med forslag ved ingen treff
  noHitsUrl?: string;
  // Tekster og andre ressurser brukt i komponenten
  resources: SearchPageResources;
  // Om oppsummering skal være tilgjengelig eller ikke
  isSummaryAvailable: boolean;
  // Om oppsummering skal være på som standard for brukere uten et lagret på/av-valg
  isSummaryEnabledByDefault?: boolean;
}

interface CurrentSearch {
  searchId: number;
  query: string;
  sokekilde?: Sokekilde;
}

/**
 * Søkesiden for helsenorge.no
 */
const SearchPage: React.FC<SearchPageProps> = props => {
  const [inputQuery, setInputQuery] = useState(props.query);
  const [currentSearch, setCurrentSearch] = useState<CurrentSearch>(() => ({
    searchId: 0,
    query: props.query,
    sokekilde: parseSokekilde(window.location.search),
  }));
  const { searchId, query, sokekilde } = currentSearch;

  const [isSummaryEnabled, setIsSummaryEnabled] = useState(() =>
    resolveInitialSummaryEnabled(props.isSummaryAvailable, props.isSummaryEnabledByDefault ?? false)
  );

  const { isSummaryExpanded, handleSummaryExpanded, returnUrl, syncReturnUrlWithLocation } = useExpandedSummary();

  const trackedSearchId = useRef<number | null>(null);

  const trackCurrentSearchOnce = (totalHits: number, searchedQuery: string): void => {
    if (trackedSearchId.current === searchId) {
      return;
    }

    trackedSearchId.current = searchId;
    trackGlobalSearch({ searchType: props.searchType, query: searchedQuery, totalHits, isSummaryEnabled, sokekilde });
  };

  // Agentsøk over websocket – eier improvedQuery, treff, oppsummering, loading og fallback
  const agent = useAgentSearch({
    language: props.language,
    initialQuery: props.query,
    initialIncludeSummary: isSummaryEnabled,
    onSearchResults: trackCurrentSearchOnce,
  });

  const activeQuery = agent.improvedQuery || query;

  // Gammelt søk: paginering, og fallback dersom websockets feiler eller ikke er tilgjengelig
  const pagination = usePaginatedSearch({
    searchUrl: props.searchUrl,
    query: activeQuery,
    contentReference: props.contentReference,
    enabled: agent.useFallback && !!activeQuery,
  });

  const { hits, totalHits, skip, take, hasNextPage } = combineSearchResults(agent.searchResponse, pagination.pagination, agent.useFallback);

  const fallbackTotalHits = agent.useFallback ? pagination.pagination?.totalHits : undefined;
  const trackFallbackSearch = useEffectEvent((): void => {
    if (fallbackTotalHits !== undefined) {
      trackCurrentSearchOnce(fallbackTotalHits, query);
    }
  });

  useEffect(() => {
    trackFallbackSearch();
  }, [fallbackTotalHits, searchId]);

  const isLoadingFirstPage = agent.isLoadingSearchResponse || (agent.useFallback && pagination.isLoading && !pagination.pagination);

  const handleInputChange = (q: string): void => setInputQuery(q);

  const handleSubmit = (q: string, submittedSokekilde?: Sokekilde): void => {
    setInputQuery(q);
    setCurrentSearch(previous => ({ searchId: previous.searchId + 1, query: q, sokekilde: submittedSokekilde }));
    agent.reset();
    setSearchParams({ q, [sokekildeSearchParam]: submittedSokekilde }, false);
    syncReturnUrlWithLocation();
    document.title = `${props.resources.searchResultTitle} - helsenorge.no`.replace('{query}', q);
    agent.search(q, isSummaryEnabled);
  };

  const handleToggleSummary = (isEnabled: boolean): void => {
    saveSummaryChoice(isEnabled);
    setIsSummaryEnabled(isEnabled);

    if (isEnabled && query) {
      agent.refreshSummary(query);
    }
  };

  const handleSummaryResultClick = (title: string, hitType: AgentHitType): void => {
    trackResultClick({
      searchType: props.searchType,
      query,
      totalHits,
      selectedResult: title,
      resultTag: hitType,
      isSummaryEnabled,
      sokekilde,
      valgtSokeresultatPlassering: 'ki-oppsummering',
    });
  };

  const handlePositiveFeedbackForSummary = (): void => {
    trackPositiveFeedbackForSummary({
      searchType: props.searchType,
      query,
      totalHits,
      isSummaryEnabled,
      sokekilde,
    });
  };

  const handleNegativeFeedbackForSummary = (): void => {
    trackNegativeFeedbackForSummary({
      searchType: props.searchType,
      query,
      totalHits,
      isSummaryEnabled,
      sokekilde,
    });
  };

  const handleSummaryExpandedWithTracking = (expanded: boolean): void => {
    handleSummaryExpanded(expanded);

    if (expanded) {
      trackExpandedSummary({ searchType: props.searchType, query, totalHits, isSummaryEnabled, sokekilde });
    }
  };

  const loadingMessages = [props.resources.loadingstart, props.resources.loadinginprogress, props.resources.loadingend];

  return (
    <div>
      <SearchTitle>{query ? props.resources.searchResultTitle.replace('{query}', query) : props.resources.title}</SearchTitle>

      <SearchInput
        query={inputQuery}
        searchUrl={props.searchUrl}
        enableAutosuggest={!props.disableAutoSuggest}
        shouldRenderSuggestions={inputQuery !== query}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        inputAriaLabel={props.resources.inputAriaLabel}
        containerAriaLabel={props.resources.containerAriaLabel}
        submitButtonText={props.resources.submitButtonText}
      />

      {props.isSummaryAvailable && (
        <Summary
          isSummaryEnabled={isSummaryEnabled}
          onToggleSummary={handleToggleSummary}
          summary={agent.searchSummary}
          onResultClick={handleSummaryResultClick}
          onPositiveFeedbackForSummary={handlePositiveFeedbackForSummary}
          onNegativeFeedbackForSummary={handleNegativeFeedbackForSummary}
          resources={{
            goToTjeneste: props.resources.goToTjeneste,
            goToVerktoy: props.resources.goToVerktoy,
            showMore: props.resources.showMore,
            showLess: props.resources.showLess,
            showSummary: props.resources.showSummary,
            betaTag: props.resources.betaTag,
            loadingSummaryUnderstanding: props.resources.loadingSummaryUnderstanding,
            loadingSummaryFetching: props.resources.loadingSummaryFetching,
            loadingSummaryOptimizing: props.resources.loadingSummaryOptimizing,
            loadingSummaryGenerating: props.resources.loadingSummaryGenerating,
            moreInformation: props.resources.moreInformation,
            summaryExplainedText: props.resources.summaryExplainedText,
            moreInformationTitle: props.resources.moreInformationTitle,
            moreInformationText: props.resources.moreInformationText,
            closeButton: props.resources.closeButton,
            summaryTitle: props.resources.summaryTitle,
            linksTitle: props.resources.linksTitle,
            actionsTitle: props.resources.actionsTitle,
            missingInformationMessage: props.resources.missingInformationMessage,
            returnUrlText: props.resources.returnUrlText,
            wasSummaryHelpful: props.resources.wasSummaryHelpful,
            summaryWasHelpfulAriaLabel: props.resources.summaryWasHelpfulAriaLabel,
            summaryWasNotHelpfulAriaLabel: props.resources.summaryWasNotHelpfulAriaLabel,
          }}
          isLoading={isSummaryEnabled && agent.isLoadingSummary}
          hasReceivedSummary={agent.hasReceivedSummary}
          isSummaryExpanded={isSummaryExpanded}
          handleSummaryExpanded={handleSummaryExpandedWithTracking}
          returnUrl={returnUrl}
        />
      )}

      <SearchStatusText totalHits={totalHits}>
        <SearchStatus
          query={query}
          isLoading={false}
          isError={pagination.isError}
          totalHits={totalHits}
          noHitsText={props.resources.noHitsText}
          noHitsUrl={props.noHitsUrl}
          noHitsLinkTitle={props.resources.noHitsLinkTitle}
          searchErrorMessage={props.resources.searchErrorMessage}
          noQueryErrorMessage={props.resources.noQueryErrorMessage}
          searchResultsStatusMessage={props.resources.searchResultsStatusMessage}
        />

        {isLoadingFirstPage && !isSummaryEnabled && <LoadingText messages={loadingMessages} />}
      </SearchStatusText>

      {isLoadingFirstPage ? (
        <SearchHitsPlaceholder />
      ) : (
        <SearchHits>
          {hits.map((hit, index) => (
            <SearchHit
              key={hit.url}
              hit={hit}
              last={index === hits.length - 1}
              returnUrl={returnUrl}
              returnUrlText={props.resources.returnUrlText}
              tjenesteHitTypeText={props.resources.tjenesteHitTypeText}
              verktoyHitTypeText={props.resources.verktoyHitTypeText}
              onClick={(): void =>
                trackResultClick({
                  searchType: props.searchType,
                  query,
                  totalHits,
                  position: index + 1,
                  resultTag: hit.hitType,
                  selectedResult: hit.title,
                  isSummaryEnabled,
                  sokekilde,
                  valgtSokeresultatPlassering: 'resultatliste',
                })
              }
            />
          ))}
        </SearchHits>
      )}

      <SearchCount hits={hits.length} totalHits={totalHits ?? 0} paginationMessage={props.resources.paginationMessage} />

      {hasNextPage && (
        <SearchLoadMoreButton
          disabled={pagination.isLoading}
          onClick={() => pagination.fetchNext([activeQuery, (skip ?? 0) + (take ?? 0)])}
        >
          {props.resources.showMoreButtonText}
        </SearchLoadMoreButton>
      )}
    </div>
  );
};

export default SearchPage;
